import type { ActionType } from "./character/character"

/** Server-authoritative animation playback for any entity, independent of attacks and input. */
export interface AnimationPoseView {
    id: string
    action: ActionType | string
    angle: number
    durationMs: number
    remainingMs: number
    /** Zero-based frame to raise to, then hold indefinitely until replaced or cleared. */
    holdFrame?: number
    /**
     * A performance (ember_sprite/animations.ts): `action` names one, played in the body's facing,
     * and when it has played the body goes back to whatever it was doing.
     */
    performance?: boolean
    /**
     * A flourish (client effects/body_flourish.ts): `action` names a render-only motion of the whole
     * body -- a binky, a startled hop -- played over whatever the body's sheet is showing. No sheet
     * row is asked for, so any body can do one.
     */
    flourish?: boolean
    /** The run-up gives this jump an extra 16px of visual height at its peak. */
    runningJump?: boolean
    /** Plays over and over until replaced or cleared: progress wraps instead of ending. */
    loop?: boolean
    /**
     * A jump up onto a wall's top (game-common/room/ember/wall_top.ts): where the drawn body comes
     * down, as an offset from where it took off (up is negative: the storey above is drawn higher),
     * and the room it took off in. It is moved up onto that storey as it comes down.
     */
    mount?: JumpMount
}

/** See `AnimationPoseView.mount`. */
export interface JumpMount {
    dx: number
    dy: number
    roomId: string
    /** The wall's cell: in the air the body sorts as on its top, over the wall's art (client entity_depth.ts). */
    x?: number
    y?: number
}

/** The flourishes a body can play (see `AnimationPoseView.flourish`), and how long each lasts. */
export const FLOURISH_DURATION_MS: Record<string, number> = {
    // The happy rabbit's leap: crouch, spring, twist in the air, land.
    binky: 760,
    // A jump on the spot, as from a fright.
    startle: 420,
    // A player's jump (the Space key): off the ground almost at once, up and down on gravity's curve.
    jump: 480,
}

/** A player's jump, as its flourish is named (server/src/jumping.ts). */
export const JUMP_FLOURISH = "jump"

/**
 * What a jump's id may be. The client that jumps names it, plays it at once, and the server sends
 * it back under that same id, so the jumper's own sprite knows the echo for the jump it already
 * played (BodyFlourish plays each id once).
 */
export const JUMP_ID = /^jump:[A-Za-z0-9_-]{1,64}:\d{1,16}$/

let flourishes = 0

/** A flourish for a body to play from `now`, facing `angle`. Every one has its own id: clients play each id once. */
export const flourishPose = (name: string, angle: number, now: number): AnimationPoseView => {
    const durationMs = FLOURISH_DURATION_MS[name]
    if (!durationMs) throw new Error(`No flourish called ${name}`)
    return { id: `flourish:${name}:${now}:${++flourishes}`, action: name, angle, durationMs, remainingMs: durationMs, flourish: true }
}

/** Holding never advances into a release on its own, including after a late snapshot. */
export const sampleAnimationPose = (view: AnimationPoseView, elapsedMs: number) => {
    if (view.loop && view.durationMs > 0) {
        const played = view.durationMs - view.remainingMs + Math.max(0, elapsedMs)
        return { action: view.action, progress: (((played % view.durationMs) + view.durationMs) % view.durationMs) / view.durationMs }
    }
    const remaining = view.remainingMs - Math.max(0, elapsedMs)
    const progress = view.durationMs > 0 ? Math.max(0, Math.min(1, 1 - remaining / view.durationMs)) : 1
    if (view.holdFrame !== undefined) return {
        action: view.action, progress: 0, frame: Math.floor(progress * view.holdFrame),
    }
    return { action: remaining <= 0 ? "idle" as const : view.action, progress }
}
